import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useAuth } from "@clerk/clerk-react";
import { fetchUserBuildings } from "../api/buildingsApi";
import {
  setBuildingContext,
  COMPANY_BUILDING,
} from "../context/BuildingContext";

interface Building {
  id: string;
  name: string;
  address: string;
  role: string;
}

const BuildingSelectionPage: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadBuildings = async () => {
      try {
        setLoading(true);
        const token = (await getToken()) || "";
        const userBuildings = await fetchUserBuildings(token);

        // If user has only one building, automatically select it
        if (userBuildings.length === 1) {
          handleBuildingSelect(userBuildings[0]);
          return;
        }

        setBuildings(userBuildings);
      } catch (err) {
        setError("Failed to load your buildings. Please try again later.");
        console.error("Error loading buildings:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBuildings();
  }, [user, getToken]);

  const handleBuildingSelect = async (building: Building) => {
    // Save selected building to local storage or context
    localStorage.setItem("currentBuilding", JSON.stringify(building));

    // Update building context
    setBuildingContext(building);

    // Redirect to dashboard
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">
            Loading your buildings...
          </h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6 bg-white shadow-md rounded-lg">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (buildings.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6 bg-white shadow-md rounded-lg">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            No Buildings Available
          </h2>
          <p className="text-gray-600 mb-4">
            You can manage company-level accounts or contact your administrator
            to add buildings.
          </p>
          <button
            onClick={() => handleBuildingSelect(COMPANY_BUILDING)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none"
          >
            Continue with Company Accounts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Select a Building
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Choose which building you want to manage
          </p>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            <li>
              <button
                onClick={() => handleBuildingSelect(COMPANY_BUILDING)}
                className="w-full px-4 py-4 flex items-center hover:bg-gray-50 focus:outline-none"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-left text-sm font-medium text-primary-600 truncate">
                    Company Accounts
                  </div>
                  <div className="mt-2 flex">
                    <div className="text-left flex items-center text-sm text-gray-500">
                      <span className="truncate">
                        Manage company-level expenses and accounts
                      </span>
                    </div>
                  </div>
                </div>
                <div className="ml-5 flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </button>
            </li>
            {buildings.map((building) => (
              <li key={building.id}>
                <button
                  onClick={() => handleBuildingSelect(building)}
                  className="w-full px-4 py-4 flex items-center hover:bg-gray-50 focus:outline-none"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-left text-sm font-medium text-primary-600 truncate">
                      {building.name}
                    </div>
                    <div className="mt-2 flex">
                      <div className="text-left flex items-center text-sm text-gray-500">
                        <span className="truncate">{building.address}</span>
                      </div>
                    </div>
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        {building.role}
                      </span>
                    </div>
                  </div>
                  <div className="ml-5 flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BuildingSelectionPage;
